// Which outbound rows were WRITTEN but never handed to Twilio.
//
// An outbound row is inserted BEFORE the send, so the row existing is not evidence the
// lead ever saw the text. Two paths leave one behind permanently:
//
//   1. `status = 'expired'` — a queued draft dropped before it drained. Written by
//      send-queued-messages with error_code `stale_message`, `assistant_replied_first`
//      or `superseded_by_newer_reply`.
//   2. `error_code = 'never_sent_invocation_died'` — processmessageai was killed by the
//      ~150s edge wall clock mid human-delay (Vault/SurFox/gotchas.md, "processmessageai
//      is ALREADY at the wall clock"). send-queued-messages stamps this ONLY after asking
//      Twilio and getting a definitive "no such message"; an unverifiable row is left
//      alone, so this stamp is ground truth, not a guess.
//
// Live 2026-08-26 this is 36 rows of 97,539 outbound (25 expired + 11 never-sent).
//
// ⚠️ DELIBERATELY NOT INCLUDED: `failed` / `undelivered` rows carrying a
// `delivery_error_code` (24,210 of them). Twilio ACCEPTED those and issued a real SID —
// the carrier dropped them afterwards. They were sent, the "⚠️ Not delivered" badge on
// them is meaningful, and hiding them would erase the carrier-filtering (30007) evidence
// the deliverability work reads. "Never sent" is a narrower claim than "never arrived".
// CommonJS on purpose, the same way src/lib/planPricing.js is: this rule has to hold on
// BOTH sides of the app, and the Express routes (`require`) and the React pages (`import`)
// have to reach the identical predicate. A second ESM copy for the frontend is exactly the
// sibling drift that gets one surface fixed and its twin left behind.
//
// ⚠️ Every caller must SELECT `direction`, `status` and `error_code`. A query that omits a
// column leaves it undefined, and undefined reads as "was sent" — which fails open, so a
// never-sent row shows rather than a real one vanishing, but it also silently does nothing.
const NEVER_SENT_ERROR_CODES = new Set(['never_sent_invocation_died']);

function wasNeverSent(msg) {
  if (!msg || msg.direction !== 'outbound') return false;
  if (msg.status === 'expired') return true;
  return NEVER_SENT_ERROR_CODES.has(msg.error_code);
}

// 🔴 ALWAYS FILTER IN JS. There is deliberately NO PostgREST equivalent of this predicate.
// `not.or=(status.eq.expired,error_code.eq.never_sent_invocation_died)` looks correct and is
// catastrophic: `error_code.eq.X` is NULL for the 97,248 outbound rows whose error_code is
// NULL, `false OR NULL` is NULL, and `NOT NULL` drops the row. Measured 2026-08-26 against
// prod, that filter returns 255 outbound rows instead of 97,503 — it hides nearly every real
// message and shows almost nothing. `reconcile-dropped-replies` filters this same set in JS
// and its comment at index.ts:198 gives the same reason. Do not reintroduce a server filter.
module.exports = {
  wasNeverSent,
  NEVER_SENT_ERROR_CODES,
};
